import React, { useState, useEffect, useRef } from 'react';
import ProtectedRoute from '../../routes/protected';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Avatar } from 'primereact/avatar';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import bootstrap from 'bootstrap/dist/js/bootstrap.bundle';
import careerService from '../../services/data/career.service';
import performanceService from '../../services/data/performance.service';
import useUserData from '../../context/user';
import useCompanyData from '../../context/company';
import UsePerms from '../../context/userAccess';
import CareerHeader from './careerHeader';
import CareerMenu from './careerMenu';
import SkeletonSummary from './skeletonSummary';
import NameInitials from '../../helpers/nameInitials';
import FormGiveFeedback from '../../components/performance/forms/formGiveFeedback';
import FeedbackReceivedList from '../../components/performance/feedbackReceivedList';

const LEVELS = [
    { min: 0,  max: 2,        label: 'Iniciante',     color: '#6c757d', icon: '🌱', next: 3  },
    { min: 3,  max: 9,        label: 'Colaborador',   color: '#0d6efd', icon: '💬', next: 10 },
    { min: 10, max: 24,       label: 'Influenciador', color: '#6610f2', icon: '⚡', next: 25 },
    { min: 25, max: 49,       label: 'Mentor',        color: '#fd7e14', icon: '🏅', next: 50 },
    { min: 50, max: Infinity, label: 'Lenda',         color: '#ffc107', icon: '🏆', next: null },
];

function getLevel(count) {
    return LEVELS.find((l) => count >= l.min && count <= l.max) || LEVELS[0];
}

export default function CareerFeedback() {

    const { id } = useParams();
    const { user } = useUserData();
    const { rootUser } = useCompanyData();
    const VerifyPerms = UsePerms();
    const navigate = useNavigate();
    const toast = useRef(null);
    const offCanvasGiveRef = useRef();
    const offCanvasGivenRef = useRef();

    const [pageLoad, setPageLoad] = useState(true);
    const [summaryLoad, setSummaryLoad] = useState(true);
    const [userEmployee, setUserEmployee] = useState('');
    const [showPerformanceTabs, setShowPerformanceTabs] = useState(false);
    const [isAnyManager, setIsAnyManager] = useState(false);
    const [summary, setSummary] = useState(null);
    const [feedbackGiven, setFeedbackGiven] = useState(false);
    const [givenList, setGivenList] = useState([]);
    const [givenLoad, setGivenLoad] = useState(false);
    const [givenNext, setGivenNext] = useState(null);
    const [givenPage, setGivenPage] = useState(1);

    useEffect(() => {
        loadEmployee();
    }, []);

    function loadEmployee() {
        const companyId = localStorage.getItem('companyId');
        careerService.getEmployeeSummary({ company_id: companyId }).then((me) => {
            if ((me.team && me.team.length > 0) || rootUser) setIsAnyManager(true);
        }).catch(() => {});
        careerService.getEmployeeSummary({ company_id: companyId, id }).then((response) => {
            setUserEmployee(response);
        }).catch((err) => {
            console.log(err.response);
        }).finally(() => setPageLoad(false));
    }

    useEffect(() => {
        if (!userEmployee) return;
        if (userEmployee.manager && userEmployee.manager.uid === user.uid) {
            setShowPerformanceTabs(true);
            return;
        }
        if (userEmployee.user.id === user.uid) {
            setShowPerformanceTabs(true);
            return;
        }
        if (VerifyPerms(['add_performancereview']) || rootUser) {
            setShowPerformanceTabs(true);
        }
    }, [userEmployee, user, rootUser]);

    useEffect(() => {
        if (id) loadSummary();
    }, [id]);

    function loadSummary() {
        setSummaryLoad(true);
        performanceService.feedbackDashboardSummary(id).then((data) => {
            setSummary(data);
        }).catch(() => {
            toast.current?.show({ severity: 'warn', summary: 'Ops', detail: 'Erro ao carregar resumo de feedback', life: 3000 });
        }).finally(() => setSummaryLoad(false));
    }

    function openGive() {
        const canvas = new bootstrap.Offcanvas(offCanvasGiveRef.current);
        canvas.show();
    }

    function openGiven() {
        const canvas = new bootstrap.Offcanvas(offCanvasGivenRef.current);
        canvas.show();
        if (givenList.length === 0) loadGiven(1);
    }

    function loadGiven(page) {
        setGivenLoad(true);
        performanceService.listFeedbackGiven(page).then((data) => {
            setGivenList((prev) => page === 1 ? data.results : [...prev, ...data.results]);
            setGivenNext(data.next);
            setGivenPage(page);
        }).catch(() => {}).finally(() => setGivenLoad(false));
    }

    function hideCanvas(ref) {
        bootstrap.Offcanvas.getInstance(ref.current)?.hide();
    }

    function onGiveSuccess() {
        hideCanvas(offCanvasGiveRef);
        setFeedbackGiven(true);
        loadSummary();
        toast.current.show({ severity: 'success', summary: '🎉 Feedback enviado!', detail: `Seu feedback para ${userEmployee?.user?.name} foi enviado.`, life: 4000 });
    }

    const isOwn = summary?.is_own ?? false;
    const level = summary ? getLevel(summary.given_count) : LEVELS[0];
    const progress = level.next
        ? Math.round(((summary?.given_count - level.min) / (level.next - level.min)) * 100)
        : 100;

    return (
        <ProtectedRoute>
            <Toast ref={toast} position="top-right" />

            <div className="container-fluid">

                <CareerHeader Employee={userEmployee} />

                <div className="my-4 mx-5 py-3 rounded-top" style={{ minHeight: '500px' }}>

                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-0">
                        <CareerMenu EmployeeId={userEmployee.id} showPerformanceTabs={showPerformanceTabs} showTransferTab={showPerformanceTabs || isAnyManager} Page="feedback" />
                    </div>

                    <div className="bg-white py-4 rounded formMarginBottom">
                        {pageLoad || summaryLoad ? (
                            <SkeletonSummary />
                        ) : (
                            <>
                                                                
                                <div className="row g-3 px-3 mb-4">

                                    {!isOwn && userEmployee && (
                                        <div className="d-flex gap-2 me-1 mb-1">
                                            <button className="btn btn-rounded btn-sm btn-outline-success" onClick={() => navigate('/feedback/request/', { state: { preselected: { id: userEmployee.id, name: userEmployee.user?.name } } })}>
                                                <FontAwesomeIcon icon={solid('paper-plane')} className="me-1" />
                                                Solicitar Feedback
                                            </button>
                                            <button className="btn btn-success btn-rounded btn-sm px-3" onClick={openGive}>
                                                <FontAwesomeIcon icon={solid('star')} className="me-1" />
                                                {feedbackGiven ? 'Dar outro Feedback' : 'Dar Feedback'}
                                            </button>
                                        </div>
                                    )}
                                    {/* Gamification / Stats */}
                                    {isOwn && (
                                        <div className="col-12 col-md-5">
                                            <div className="card border-0 shadow-sm h-100" style={{ background: 'linear-gradient(135deg, #f8f9ff, #e8f4fd)' }}>
                                                <div className="card-body">
                                                    <div className="d-flex align-items-center gap-3 mb-3">
                                                        <div className="rounded-circle d-flex align-items-center justify-content-center"
                                                            style={{ width: 60, height: 60, fontSize: 28, background: '#fff', border: `3px solid ${level.color}`, flexShrink: 0 }}>
                                                            {level.icon}
                                                        </div>
                                                        <div>
                                                            <p className="text-muted small mb-0">Seu nível de feedback</p>
                                                            <h5 className="fw-bold mb-0" style={{ color: level.color }}>{level.label}</h5>
                                                            <span className="small text-muted">{summary?.given_count || 0} feedbacks dados</span>
                                                        </div>
                                                    </div>
                                                    {level.next ? (
                                                        <>
                                                            <p className="small text-muted mb-1">
                                                                Próximo: <strong>{LEVELS[LEVELS.indexOf(level) + 1]?.label}</strong> — {level.next - (summary?.given_count || 0)} restantes
                                                            </p>
                                                            <ProgressBar value={progress} style={{ height: 8 }} showValue={false} />
                                                        </>
                                                    ) : (
                                                        <p className="small fw-bold text-warning mb-0">🏆 Nível máximo atingido!</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className={`col-6 ${isOwn ? 'col-md-2' : 'col-md-3'}`}>
                                        <div
                                            role="button"
                                            className="card border-0 shadow-sm h-100 text-center"
                                            style={{ cursor: 'pointer', transition: 'box-shadow .15s' }}
                                            onClick={openGiven}
                                            onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.12)'}
                                            onMouseLeave={(e) => e.currentTarget.style.boxShadow = ''}
                                        >
                                            <div className="card-body d-flex flex-column justify-content-center">
                                                <FontAwesomeIcon icon={solid('paper-plane')} className="mb-2 text-green" size="2x" />
                                                <h3 className="fw-bold text-green mb-0">{summary?.given_count || 0}</h3>
                                                <p className="text-muted small mb-0">Feedbacks Dados</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={`col-6 ${isOwn ? 'col-md-2' : 'col-md-3'}`}>
                                        <div className="card border-0 shadow-sm h-100 text-center">
                                            <div className="card-body d-flex flex-column justify-content-center">
                                                <FontAwesomeIcon icon={solid('inbox')} className="mb-2 text-green" size="2x" />
                                                <h3 className="fw-bold text-green mb-0">{summary?.received_count || 0}</h3>
                                                <p className="text-muted small mb-0">Recebidos</p>
                                            </div>
                                        </div>
                                    </div>

                                    {isOwn && (
                                        <div className="col-12 col-md-3">
                                            <div className="card border-0 shadow-sm h-100 text-center">
                                                <div className="card-body d-flex flex-column justify-content-center">
                                                    <FontAwesomeIcon icon={solid('bell')} className={`mb-2 ${summary?.pending_requests > 0 ? 'text-warning' : 'text-muted'}`} size="2x" />
                                                    <h3 className={`fw-bold mb-0 ${summary?.pending_requests > 0 ? 'text-warning' : 'text-muted'}`}>{summary?.pending_requests || 0}</h3>
                                                    <p className="text-muted small mb-0">Pendentes para responder</p>
                                                    {summary?.pending_requests > 0 && (
                                                        <a href="/feedback/pending/" className="badge bg-warning text-dark mt-1 text-decoration-none">Ver agora</a>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Feedbacks Recebidos + Leaderboard */}
                                <div className="row g-3 px-3">
                                    <div className="col-12 col-lg-7">
                                        <div className="card border-0 shadow-sm">
                                            <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                                                <span className="fw-bold text-muted">
                                                    <FontAwesomeIcon icon={solid('comment-dots')} className="me-2 text-green" />
                                                    Feedbacks Recebidos
                                                </span>
                                            </div>
                                            <div className="card-body">
                                                <FeedbackReceivedList
                                                    employeeId={id}
                                                    onRequest={isOwn ? () => navigate('/feedback/request/') : () => navigate('/feedback/request/', { state: { preselected: { id: userEmployee.id, name: userEmployee.user?.name } } })}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Leaderboard */}
                                    <div className="col-12 col-lg-5">
                                        <div className="card border-0 shadow-sm">
                                            <div className="card-header bg-white border-0">
                                                <span className="fw-bold text-muted">
                                                    <FontAwesomeIcon icon={solid('trophy')} className="me-2 text-warning" />
                                                    Ranking de Feedbacks
                                                </span>
                                            </div>
                                            <div className="card-body p-0">
                                                {summary?.leaderboard?.length > 0 ? (
                                                    <ul className="list-group list-group-flush">
                                                        {summary.leaderboard.map((entry) => (
                                                            <li key={entry.employee_id} className="list-group-item px-3 py-2 d-flex align-items-center gap-3">
                                                                <span className="fw-bold text-muted" style={{ width: 28, textAlign: 'center' }}>
                                                                    {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : `#${entry.rank}`}
                                                                </span>
                                                                <Avatar
                                                                    label={NameInitials(entry.name)}
                                                                    shape="circle"
                                                                    size="small"
                                                                    style={{ backgroundColor: '#dee2e6', color: '#495057' }}
                                                                />
                                                                <span className="flex-grow-1 small fw-semibold">{entry.name}</span>
                                                                <span className="badge bg-success rounded-pill">{entry.total}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <div className="text-center py-5 text-muted">
                                                        <FontAwesomeIcon icon={solid('trophy')} size="2x" className="mb-2 d-block mx-auto" />
                                                        <p className="small">Seja o primeiro!</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Offcanvas: Feedbacks Enviados */}
            <div className="offcanvas offcanvas-end" tabIndex="-1" ref={offCanvasGivenRef} style={{ width: 480 }}>
                <div className="offcanvas-header border-bottom">
                    <h6 className="offcanvas-title fw-bold">
                        <FontAwesomeIcon icon={solid('paper-plane')} className="me-2 text-green" />
                        Feedbacks Enviados
                    </h6>
                    <button type="button" className="btn-close" onClick={() => hideCanvas(offCanvasGivenRef)} />
                </div>
                <div className="offcanvas-body p-0">
                    {givenLoad && givenList.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <span className="spinner-border spinner-border-sm me-2" />
                            Carregando...
                        </div>
                    ) : givenList.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <FontAwesomeIcon icon={solid('paper-plane')} size="2x" className="mb-2 d-block mx-auto" />
                            <p className="small">Nenhum feedback enviado ainda.</p>
                        </div>
                    ) : (
                        <ul className="list-group list-group-flush">
                            {givenList.map((fb) => (
                                <li key={fb.id} className="list-group-item px-4 py-3">
                                    <div className="d-flex justify-content-between align-items-start gap-2">
                                        <div className="flex-grow-1">
                                            <p className="fw-semibold small mb-1">{fb.employee_name}</p>
                                            {fb.comment && (
                                                <p className="text-muted small mb-1"
                                                    style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                                                    dangerouslySetInnerHTML={{ __html: fb.comment }}
                                                />
                                            )}
                                        </div>
                                        <span className="badge bg-success bg-opacity-10 text-success small text-nowrap">
                                            {fb.rating_display}
                                        </span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                    {givenNext && (
                        <div className="text-center py-3">
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                disabled={givenLoad}
                                onClick={() => loadGiven(givenPage + 1)}
                            >
                                {givenLoad ? <span className="spinner-border spinner-border-sm" /> : 'Carregar mais'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Offcanvas: Dar Feedback */}
            <div className="offcanvas offcanvas-end" tabIndex="-1" ref={offCanvasGiveRef}>
                <div className="offcanvas-header border-bottom">
                    <h6 className="offcanvas-title fw-bold">
                        Dar Feedback para {userEmployee?.user?.name}
                    </h6>
                    <button type="button" className="btn-close" onClick={() => hideCanvas(offCanvasGiveRef)} />
                </div>
                <div className="offcanvas-body">
                    {userEmployee && (
                        <FormGiveFeedback
                            employee={{ id: userEmployee.id }}
                            feedbackRequest={null}
                            onSuccess={onGiveSuccess}
                            onCancel={() => hideCanvas(offCanvasGiveRef)}
                        />
                    )}
                </div>
            </div>

        </ProtectedRoute>
    );
}
